import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { obterSessao } from '../services/usuarios';
import './PortalCapturaNfe.css';

const ITENS_POR_PAGINA = 12;

const emitentes = [
  { nome: 'Distribuidora Alfa Ltda', cnpj: '14.200.123/0001-87' },
  { nome: 'Comercial Beta S.A.', cnpj: '33.011.244/0001-60' },
  { nome: 'Indústria Gama ME', cnpj: '08.765.432/0001-11' },
  { nome: 'Logística Delta Eireli', cnpj: '55.667.788/0001-99' },
  { nome: 'Papelaria Épsilon Ltda', cnpj: '22.110.033/0001-44' },
  { nome: 'Tech Zeta Informática', cnpj: '99.001.122/0001-33' },
  { nome: 'Alimentos Ômega Ltda', cnpj: '44.115.566/0001-22' },
  { nome: 'Serviços Sigma Ltda', cnpj: '77.889.900/0001-55' },
  { nome: 'Metalúrgica Lambda Ltda', cnpj: '12.345.678/0001-90' },
  { nome: 'Farmácia Kappa S.A.', cnpj: '65.432.109/0001-08' },
];

const statusLista = ['Autorizada', 'Autorizada', 'Autorizada', 'Cancelada', 'Autorizada', 'Pendente', 'Denegada', 'Autorizada'];

function pad(valor, tamanho) {
  return String(valor).padStart(tamanho, '0');
}

function formatarDataBr(data) {
  return data.toLocaleDateString('pt-BR');
}

const notasFiscais = Array.from({ length: 36 }, (_, index) => {
  const emitente = emitentes[index % emitentes.length];
  const numero = pad(12847 - index * 17, 9);
  const serie = String((index % 3) + 1);
  const emissao = new Date(2026, 7, 12);
  emissao.setDate(emissao.getDate() - index);
  const raizChave = `352608${pad(index + 1, 14)}${pad(Number(numero), 9)}${pad(index + 11, 9)}`;

  return {
    id: index + 1,
    numero,
    serie,
    chave: raizChave.slice(0, 44),
    emitente: emitente.nome,
    cnpj: emitente.cnpj,
    emissao: formatarDataBr(emissao),
    valor: Number((480 + index * 137.35 + (index % 5) * 820).toFixed(2)),
    status: statusLista[index % statusLista.length],
  };
});

function formatarMoeda(valor) {
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function parseDataBr(dataBr) {
  const [dia, mes, ano] = dataBr.split('/');
  return new Date(Number(ano), Number(mes) - 1, Number(dia));
}

function noInicioDoDia(dataIso) {
  const [ano, mes, dia] = dataIso.split('-').map(Number);
  return new Date(ano, mes - 1, dia);
}

function noFimDoDia(dataIso) {
  const [ano, mes, dia] = dataIso.split('-').map(Number);
  return new Date(ano, mes - 1, dia, 23, 59, 59, 999);
}

function statusClass(status) {
  if (status === 'Autorizada') return 'nfe-status nfe-status-ok';
  if (status === 'Cancelada') return 'nfe-status nfe-status-cancel';
  if (status === 'Denegada') return 'nfe-status nfe-status-deny';
  return 'nfe-status nfe-status-pending';
}

function PortalCapturaNfe() {
  const sessao = obterSessao();
  const [busca, setBusca] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [pagina, setPagina] = useState(1);
  const [menuAtivo, setMenuAtivo] = useState('consulta');

  const notasFiltradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();

    return notasFiscais.filter((nfe) => {
      const textoOk = !termo || [nfe.numero, nfe.emitente, nfe.cnpj, nfe.chave, nfe.status]
        .join(' ')
        .toLowerCase()
        .includes(termo);

      const emissao = parseDataBr(nfe.emissao);
      const inicioOk = !dataInicio || emissao >= noInicioDoDia(dataInicio);
      const fimOk = !dataFim || emissao <= noFimDoDia(dataFim);

      return textoOk && inicioOk && fimOk;
    });
  }, [busca, dataInicio, dataFim]);

  const totalPaginas = Math.ceil(notasFiltradas.length / ITENS_POR_PAGINA);
  const mostrarPaginacao = notasFiltradas.length > ITENS_POR_PAGINA;
  const paginaAtual = Math.min(pagina, Math.max(totalPaginas, 1));
  const notasPagina = notasFiltradas.slice(
    (paginaAtual - 1) * ITENS_POR_PAGINA,
    paginaAtual * ITENS_POR_PAGINA
  );

  useEffect(() => {
    setPagina(1);
  }, [busca, dataInicio, dataFim]);

  const limparFiltros = () => {
    setBusca('');
    setDataInicio('');
    setDataFim('');
    setPagina(1);
  };

  if (!sessao?.token) {
    return <Navigate to="/portal" replace />;
  }

  return (
    <div className="captura-nfe-page">
      <aside className="captura-nfe-sidebar">
        <p className="captura-nfe-brand">Captura de NFe</p>
        <p className="captura-nfe-brand-sub">SEFAZ</p>

        <nav className="captura-nfe-menu">
          <button
            type="button"
            className={menuAtivo === 'consulta' ? 'active' : ''}
            onClick={() => setMenuAtivo('consulta')}
          >
            Consulta de notas fiscais
          </button>
        </nav>

        <Link to="/portal" className="captura-nfe-voltar">
          ← Voltar aos sistemas
        </Link>
      </aside>

      <section className="captura-nfe-content">
        <header className="captura-nfe-header">
          <div>
            <h1>Consulta de notas fiscais</h1>
            <p>Notas emitidas contra o CNPJ da empresa, capturadas no SEFAZ.</p>
          </div>
          <span className="captura-nfe-count">{notasFiltradas.length} notas</span>
        </header>

        <div className="captura-nfe-toolbar">
          <label className="captura-nfe-field">
            <span>Busca</span>
            <input
              type="search"
              placeholder="Número, emitente, CNPJ, chave ou status"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </label>
          <label className="captura-nfe-field">
            <span>Data inicial</span>
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              max={dataFim || undefined}
            />
          </label>
          <label className="captura-nfe-field">
            <span>Data final</span>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              min={dataInicio || undefined}
            />
          </label>
          <button type="button" className="captura-nfe-limpar" onClick={limparFiltros}>
            Limpar filtros
          </button>
        </div>

        <div className="captura-nfe-table-wrap">
          <table className="captura-nfe-table">
            <thead>
              <tr>
                <th>Número</th>
                <th>Série</th>
                <th>Emitente</th>
                <th>CNPJ</th>
                <th>Emissão</th>
                <th>Valor</th>
                <th>Status</th>
                <th>Chave de acesso</th>
              </tr>
            </thead>
            <tbody>
              {notasPagina.map((nfe) => (
                <tr key={nfe.id}>
                  <td>{nfe.numero}</td>
                  <td>{nfe.serie}</td>
                  <td>{nfe.emitente}</td>
                  <td>{nfe.cnpj}</td>
                  <td>{nfe.emissao}</td>
                  <td className="nfe-valor">{formatarMoeda(nfe.valor)}</td>
                  <td>
                    <span className={statusClass(nfe.status)}>{nfe.status}</span>
                  </td>
                  <td className="nfe-chave">{nfe.chave}</td>
                </tr>
              ))}
              {notasFiltradas.length === 0 && (
                <tr>
                  <td colSpan={8} className="nfe-empty">
                    Nenhuma nota encontrada para o filtro informado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {mostrarPaginacao && (
          <div className="captura-nfe-paginacao">
            <button
              type="button"
              disabled={paginaAtual <= 1}
              onClick={() => setPagina((atual) => Math.max(1, atual - 1))}
            >
              Anterior
            </button>
            <div className="captura-nfe-paginas">
              {Array.from({ length: totalPaginas }, (_, index) => (
                <button
                  key={index + 1}
                  type="button"
                  className={paginaAtual === index + 1 ? 'active' : ''}
                  onClick={() => setPagina(index + 1)}
                >
                  {index + 1}
                </button>
              ))}
            </div>
            <button
              type="button"
              disabled={paginaAtual >= totalPaginas}
              onClick={() => setPagina((atual) => Math.min(totalPaginas, atual + 1))}
            >
              Próxima
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

export default PortalCapturaNfe;
